import {
  AfterViewInit,
  Component, EventEmitter,
  Input,
  OnDestroy,
  OnInit, Output,
  ViewEncapsulation
} from '@angular/core';
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import { Feature, View } from "ol";
import Map from 'ol/Map';
import Geolocation from 'ol/Geolocation.js';
import {Geometry, LineString, Point} from "ol/geom";
import { Circle as CircleStyle, Fill, Stroke, Style, Icon } from 'ol/style.js';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { defaults as defaultControls } from 'ol/control.js';

import { Subscription } from "rxjs";
import { fromLonLat, transform } from 'ol/proj';
import { StoreService } from '../../../../services/store.service';
import {
  createProjectionName,
  getSpanColor,
  getTowerIcon,
  registerProjection,
  setDefectsNoSpanText,
  setDefectsNoTowerText, setTasksOrder
} from '../../utils/map.utils';
import { LAYER_SYMBOL } from '../../config/map.config';
import { PowerLineConfig } from '../../../../model/general.model';
import { FeatureLike } from "ol/Feature";
import { BottomSheetService } from "../../../../services/bottom-sheet.service";
import { DeviceDetectorService } from 'ngx-device-detector';
import { PALETTE } from '../../../../config/general.config';
import { Extent } from 'ol/extent';
import { SpanMapModel, TowerMapModel } from '../../../../model/map.model';
import { TASK_TYPE } from '../../../go/model/task.model';
import { DialogService } from "../../../../services/dialog.service";

@Component({
  selector: 'map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() enableGeolocation = false;
  @Input() showError = false;
  @Input() coordinates: number[];
  @Input() fullPage = false;
  @Input() isDynamicControl = false;

  geolocationOn = false;
  private zoomOnNextGeolocation = false;

  public map!: Map
  geolocation: Geolocation;

  positionFeature = new Feature();
  subscription = new Subscription();

  @Output() coordinatesEvent = new EventEmitter<number[]>;

  private onGeoError = (e: any) => {
    console.log(e);
    this.dialogService.showInfoDialog({
      header: 'Błąd geolokalizacji',
      content: `KOD: ${e.code}, OPIS: ${e.message}, TYP: ${e.type}`,
      confirmBtn: 'OK'
    })
  };

  constructor(
    public store: StoreService,
    private bottomSheetService: BottomSheetService,
    private deviceService: DeviceDetectorService,
    private dialogService: DialogService
  ) {
  }

  ngOnInit(): void {
    registerProjection(this.store.selectedProject?.projection!);
    this.subscription.add(
      this.store.getPowerLineConfig().subscribe(lines => {
        if (lines) {
          this.drawLines(lines);
        }
      })
    )
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  initMap(): void {

    const center = transform([19, 52], 'EPSG:4326', createProjectionName(this.store.selectedProject?.projection!));

    this.map = new Map({
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        new VectorLayer({
          source: new VectorSource({
            features: [this.positionFeature],
          }),
          properties: {
            symbol: LAYER_SYMBOL.GEOLOCATION
          }
        }),
      ],
      view: new View({
        center: center,
        zoom: 6,
        projection: createProjectionName(this.store.selectedProject?.projection!),
      }),
      target: 'map',
      controls: defaultControls(),
    });

    this.geolocation = new Geolocation({
      trackingOptions: {
        enableHighAccuracy: true,
      },
      projection: this.map.getView().getProjection(),
    });

    this.positionFeature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({
            color: '#00BCD4',
          }),
          stroke: new Stroke({
            color: '#fff',
            width: 2,
          }),
        }),
      })
    );

    this.geolocation.addEventListener("change:position", () => this.handleGeolocation());
    this.geolocation.addEventListener('error', this.onGeoError);

    this.map.on('click', (event) => {
      const selected = this.map.forEachFeatureAtPixel(event.pixel, feature => {
        return feature;
      });

      if (selected) {
        this._clickOnFeature(selected);
      }
    });

    this.map.on("pointermove", (evt) => {
      let hit = this.map.forEachFeatureAtPixel(evt.pixel, () => {
        return true;
      });
      if (hit) {
        this.map.getTargetElement().style.cursor = 'pointer';
      } else {
        this.map.getTargetElement().style.cursor = '';
      }
    });

    if (this.enableGeolocation && !this.coordinates) {
      this._enableGeolocation(true);
    }

    if (this.coordinates) {
      this.setCoordinates();
    }
  }

  private setCoordinates(): void {
    this.positionFeature.setGeometry(new Point(this.coordinates));
    this._zoomToFeature(this.positionFeature);
    this.geolocation.setTracking(false);
    this.geolocationOn = false;
  }


  private _clickOnFeature(feature: FeatureLike): void {
    const properties = feature.getProperties();
    const attributes = properties['data'];
    const tasks = properties['tasks'];
    const type = properties['type'];
    const longitude = properties['longitude'];
    const latitude = properties['latitude'];
    const isDynamicControl = this.isDynamicControl;

    if (attributes || tasks) {
      this.bottomSheetService.open({
        attributes,
        tasks,
        isDynamicControl,
        type,
        longitude,
        latitude
      });
    }
  }

  private _enableGeolocation(withZoom = false): void {
    this.geolocationOn = true;
    this.zoomOnNextGeolocation = this.zoomOnNextGeolocation || withZoom;
    this.geolocation.setTracking(true);
  }

  handlePointFeature(coordinates: number[]): void {
    this.positionFeature.setGeometry(new Point(coordinates));
    this.coordinatesEvent.emit(coordinates);
    this.showError = false;

    if (this.isDynamicControl) {
      this.geolocation.setTracking(false);
      this.geolocationOn = false;
    }
  }

  handleGeolocation(withZoom = false): void {

    if (!this.geolocationOn) {
      this._enableGeolocation(withZoom);
      return;
    }
    const coordinates = this.geolocation.getPosition();

    if (coordinates) {
      const shouldZoom = withZoom || this.zoomOnNextGeolocation;
      this.zoomOnNextGeolocation = false;
      this.handlePointFeature(coordinates);
      if (shouldZoom) {
        this._zoomToFeature(this.positionFeature);
      }
    }
  }

  private _zoomToFeature(feature: Feature<Geometry>): void {
    const extent = feature.getGeometry()?.getExtent();

    if (extent) {
      this.zoomToExtent(extent);
    }
  }

  ngOnDestroy() {
    this.geolocation.setTracking(false);
    this.geolocation.dispose();
    this.geolocation.removeEventListener("change:position", () => {});
    this.geolocation.removeEventListener('error', this.onGeoError);
    this.map.removeEventListener("click", () => {});
    this.map.removeEventListener("pointermove", () => {})
    this.map.dispose();
    this.subscription.unsubscribe();
  }

  private drawLines(config: PowerLineConfig): void {
    const layer = this.map.getLayers().getArray().find(item => item.get('name') === LAYER_SYMBOL.POWER_LINE);
    if (layer) {
      this.map.removeLayer(layer);
    }

    const vectorSource = new VectorSource();

    config.spans.forEach(span => {
      if (config.onlyTasks && span.tasks.length === 0) return;

      const feature = this.createLineFeature(span);
      vectorSource.addFeature(feature);
    });

    config.towers.forEach(tower => {
      if (config.onlyTasks && tower.tasks.length === 0) return;

      const point = this.createTowerFeature(tower);
      vectorSource.addFeature(point);
    });

    const vectorLayer = new VectorLayer({ source: vectorSource });
    vectorLayer.set('name', LAYER_SYMBOL.POWER_LINE);
    this.map.addLayer(vectorLayer);

    if (config.zoom) {
      const source = vectorLayer.getSource();
      if (source) {
        const extent = source.getExtent();
        if (extent) {
          this.zoomToExtent(extent)
        }
      }
    }
  }

  private createLineFeature(span: SpanMapModel): Feature {
    const start = fromLonLat([span.startTower.longitude, span.startTower.latitude], this.map.getView().getProjection());
    const end = fromLonLat([span.endTower.longitude, span.endTower.latitude], this.map.getView().getProjection());
    const line = new LineString([start, end]);

    const feature = new Feature({ geometry: line });
    feature.setStyle([
      this.createLineStyle(PALETTE.BLACK, span, this.deviceService.isMobile() ? 14 : 6),
      this.createLineStyle(getSpanColor(span), span, this.deviceService.isMobile() ? 12 : 4)
    ]);
    feature.setProperties({
      data: span.attributes,
      tasks: setTasksOrder(span.tasks),
      type: TASK_TYPE.SPAN
    });

    return feature;
  }

  private createLineStyle(color: string, span: SpanMapModel, width: number): Style {
    return new Style({
      stroke: new Stroke({ color, width }),
      text: setDefectsNoSpanText(span.tasks)
    });
  }

  private createTowerFeature(tower: TowerMapModel): Feature {
    const point = new Feature({
      geometry: new Point(fromLonLat([tower.longitude, tower.latitude], this.map.getView().getProjection()))
    });

    point.setStyle(new Style({
      image: new Icon({
        src: getTowerIcon(tower),
        scale: 0.05
      }),
      text: setDefectsNoTowerText(tower.tasks)
    }));

    point.setProperties({
      data: tower.attributes,
      tasks: setTasksOrder(tower.tasks),
      type: TASK_TYPE.TOWER,
      longitude: tower.longitude,
      latitude: tower.latitude
    });

    return point;
  }
  private zoomToExtent(extent: Extent) {
    this.map.getView().fit(extent, {
      size: this.map.getSize(),
      padding: [20, 20, 20, 20],
      duration: 300,
      maxZoom: 17,
    });
  }
}
